import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import PrintIcon from '@mui/icons-material/Print';
import {theme} from '../../theme/theme';
import {
  Payment,
  PropertyInvoiceWithRelations,
  useGetPropertyByIdQuery,
  useGetPaymentsUserQuery,
} from '../../redux/residentPortalApi';
import {formatCurrency} from '../../utils/currencyUtil';
import ResponsiveRecordCard from './ResponsiveRecordCard';

type StatementRow = {
  id: string;
  date: Date | null;
  dateLabel: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  kind: 'debit' | 'credit';
  status: string;
  outstandingAmount: number;
  excludeFromCalculations: boolean;
};

interface OwnProps {
  propertyId: string;
  invoices?: PropertyInvoiceWithRelations[];
}

const isSucceededLevyPayment = (payment: Payment) => {
  const purpose = (payment.purpose || '').toLowerCase();
  const status = (payment.status || '').toLowerCase();
  const succeededStatuses = ['succeeded', 'success', 'successful', 'paid', 'completed', 'complete'];
  return purpose === 'levies' && succeededStatuses.includes(status);
};

const isCancelledInvoice = (invoice: PropertyInvoiceWithRelations) => {
  return (invoice?.status || '').toString().toLowerCase() === 'cancelled';
};

const normalizeStatementStatus = (kind: 'debit' | 'credit', rawStatus: string, outstandingAmount: number) => {
  if (kind === 'credit') {
    return 'Paid';
  }

  const normalized = (rawStatus || '').toLowerCase();
  if (normalized === 'paid' || outstandingAmount <= 0) {
    return 'Paid';
  }

  return 'Unpaid';
};

const capitalizeFirstLetter = (value?: string) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const LeviesStatement: React.FC<OwnProps> = ({propertyId, invoices = []}) => {
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const [monthFilter, setMonthFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [outstandingOnly, setOutstandingOnly] = useState(false);

  const {data: payments = [], isLoading, isError} = useGetPaymentsUserQuery(
    {propertyId},
    {skip: !propertyId}
  );
  const {data: property} = useGetPropertyByIdQuery(
    {id: propertyId},
    {skip: !propertyId}
  );

  const statementRows = useMemo(() => {
    const invoiceRows: StatementRow[] = invoices.map((invoice) => {
      const amount = Number(invoice?.invoice?.totalCost || 0);
      const outstandingAmount = Number(invoice?.propertyInvoiceBalance ?? 0);
      const dueDateRaw = invoice?.invoice?.dueDate || invoice?.dueDate;
      const dueDate = dueDateRaw ? new Date(dueDateRaw) : null;
      const dateLabel = dueDate ? dueDate.toLocaleDateString('en-GB', { timeZone: 'UTC' }) : 'No date';
      const status = (invoice?.status || 'posted').toString();
      const cancelled = isCancelledInvoice(invoice);
      const displayStatus = normalizeStatementStatus('debit', status, outstandingAmount);

      return {
        id: `invoice-${invoice.id || invoice.invoiceNumber}`,
        date: dueDate,
        dateLabel,
        description: 'Levy Invoice',
        reference: invoice.invoiceNumber || 'Invoice',
        debit: amount,
        credit: 0,
        kind: 'debit',
        status: cancelled ? 'Cancelled' : displayStatus,
        outstandingAmount,
        excludeFromCalculations: cancelled,
      };
    });

    const paymentRows: StatementRow[] = payments
      .filter(isSucceededLevyPayment)
      .map((payment, index) => {
        const amount = Number(payment.amount || 0) / 100;
        const paymentDate = payment.paymentDate ? new Date(payment.paymentDate) : null;
        const dateLabel = paymentDate ? paymentDate.toLocaleDateString('en-GB') : 'No date';

        return {
          id: `payment-${payment.id || payment.paymentIntentId || `${payment.paymentDate || 'nodate'}-${payment.amount || 0}-${index}`}`,
          date: paymentDate,
          dateLabel,
          description: 'Levy Payment',
          reference: payment.paymentIntentId || payment.id || 'Payment',
          debit: 0,
          credit: amount,
          kind: 'credit',
          status: normalizeStatementStatus('credit', payment.status || 'succeeded', 0),
          outstandingAmount: 0,
          excludeFromCalculations: false,
        };
      });

    return [...invoiceRows, ...paymentRows].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.getTime() - b.date.getTime();
    });
  }, [invoices, payments]);

  const monthOptions = useMemo(() => {
    const map = new Map<string, string>();
    statementRows.forEach((row) => {
      if (!row.date) return;
      const key = `${row.date.getFullYear()}-${String(row.date.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) {
        map.set(key, row.date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
      }
    });

    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([value, label]) => ({ value, label }));
  }, [statementRows]);

  const statusOptions = useMemo(() => {
    const statuses = Array.from(new Set(statementRows.map((row) => row.status.toLowerCase())));
    return statuses.sort();
  }, [statementRows]);

  const filteredRows = useMemo(() => {
    return statementRows.filter((row) => {
      const monthMatch = monthFilter === 'all'
        ? true
        : row.date
          ? `${row.date.getFullYear()}-${String(row.date.getMonth() + 1).padStart(2, '0')}` === monthFilter
          : false;

      const statusMatch = statusFilter === 'all'
        ? true
        : row.status.toLowerCase() === statusFilter;

      const outstandingMatch = outstandingOnly
        ? row.kind === 'debit' && !row.excludeFromCalculations && row.outstandingAmount > 0
        : true;

      return monthMatch && statusMatch && outstandingMatch;
    });
  }, [monthFilter, outstandingOnly, statementRows, statusFilter]);

  const summary = useMemo(() => {
    const totalDebit = filteredRows.reduce((sum, row) => sum + (row.excludeFromCalculations ? 0 : row.debit), 0);
    const totalCredit = filteredRows.reduce((sum, row) => sum + (row.excludeFromCalculations ? 0 : row.credit), 0);
    return {
      totalDebit,
      totalCredit,
      balance: totalDebit - totalCredit,
    };
  }, [filteredRows]);

  const rowsWithRunningBalance = useMemo(() => {
    let running = 0;
    return filteredRows.map((row) => {
      if (!row.excludeFromCalculations) {
        running += row.debit - row.credit;
      }
      return {
        ...row,
        runningBalance: running,
      };
    });
  }, [filteredRows]);

  const printStatement = () => {
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (!printWindow) return;

    const rowsHtml = rowsWithRunningBalance
      .map((row) => `
        <tr>
          <td>${row.dateLabel}</td>
          <td>${row.description}</td>
          <td>${row.reference}</td>
          <td>${row.status}</td>
          <td style="color:#b42318;font-weight:700;">${row.debit > 0 ? formatCurrency(row.debit) : '-'}</td>
          <td style="color:#027a48;font-weight:700;">${row.credit > 0 ? formatCurrency(row.credit) : '-'}</td>
          <td style="font-weight:700;">${formatCurrency(row.runningBalance)}</td>
        </tr>`)
      .join('');

    const activeMonth = monthFilter === 'all' ? 'All' : (monthOptions.find((m) => m.value === monthFilter)?.label || monthFilter);
    const activeStatus = statusFilter === 'all' ? 'All' : statusFilter;
    const propertyLabel = property
      ? `${capitalizeFirstLetter(property.development)}${property.phase ? ` - ${capitalizeFirstLetter(property.phase)}` : ''}${property.unitNumber ? ` Unit ${property.unitNumber}` : ''}`.trim()
      : propertyId;
    const propertyType = property?.type || 'N/A';

    printWindow.document.write(`
      <html>
      <head>
        <title>Levies Statement</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
          h1 { margin: 0 0 8px 0; color: #0f265c; }
          .meta { margin: 0 0 16px 0; color: #334155; font-size: 14px; }
          .summary { display: flex; gap: 16px; margin-bottom: 16px; }
          .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; min-width: 180px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #e5e7eb; padding: 10px; font-size: 13px; text-align: left; }
          th { background: #f8fafc; }
        </style>
      </head>
      <body>
        <h1>Levies Statement</h1>
        <p class="meta">Property: ${propertyLabel}</p>
        <p class="meta">Type: ${propertyType}</p>
        <p class="meta">Filters: Month = ${activeMonth}, Status = ${activeStatus}, Outstanding only = ${outstandingOnly ? 'Yes' : 'No'}</p>
        <div class="summary">
          <div class="card"><div>Total Debit</div><strong>${formatCurrency(summary.totalDebit)}</strong></div>
          <div class="card"><div>Total Credit</div><strong>${formatCurrency(summary.totalCredit)}</strong></div>
          <div class="card"><div>Outstanding Balance</div><strong>${formatCurrency(summary.balance)}</strong></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Reference</th>
              <th>Status</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: {xs: 2, sm: 3, md: 4},
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4,
      }}
    >
      <Stack spacing={2.5}>
        <Typography
          variant="h2"
          sx={{
            fontSize: {xs: '1.3rem', sm: '1.5rem', md: '1.75rem'},
            fontWeight: 700,
            color: '#0f265c',
            mb: 1,
            borderBottom: '2px solid #e0e0e0',
            paddingBottom: 1,
          }}
        >
          Levies Statement
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))'},
            gap: 1.5,
          }}
        >
          <Paper elevation={0} sx={{p: 1.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#fff8f8'}}>
            <Typography variant="caption" color="text.secondary">Total Debit</Typography>
            <Typography sx={{fontWeight: 700, color: '#b42318'}}>{formatCurrency(summary.totalDebit)}</Typography>
          </Paper>
          <Paper elevation={0} sx={{p: 1.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f6fffa'}}>
            <Typography variant="caption" color="text.secondary">Total Credit</Typography>
            <Typography sx={{fontWeight: 700, color: '#027a48'}}>{formatCurrency(summary.totalCredit)}</Typography>
          </Paper>
          <Paper elevation={0} sx={{p: 1.5, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#f8fbff'}}>
            <Typography variant="caption" color="text.secondary">Outstanding Balance</Typography>
            <Typography sx={{fontWeight: 700, color: summary.balance > 0 ? '#b42318' : '#027a48'}}>{formatCurrency(summary.balance)}</Typography>
          </Paper>
        </Box>

        <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} alignItems={{xs: 'stretch', md: 'center'}}>
          <FormControl size="small" sx={{minWidth: 170}}>
            <InputLabel id="statement-month-label">Month</InputLabel>
            <Select
              labelId="statement-month-label"
              value={monthFilter}
              label="Month"
              onChange={(event: SelectChangeEvent) => setMonthFilter(event.target.value)}
            >
              <MenuItem value="all">All months</MenuItem>
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 170}}>
            <InputLabel id="statement-status-label">Status</InputLabel>
            <Select
              labelId="statement-status-label"
              value={statusFilter}
              label="Status"
              onChange={(event: SelectChangeEvent) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="all">All statuses</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{status === 'paid' ? 'Paid' : status === 'unpaid' ? 'Unpaid' : status}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={outstandingOnly}
                onChange={(event) => setOutstandingOnly(event.target.checked)}
              />
            }
            label="Outstanding invoices only"
          />

          <Box sx={{ml: {md: 'auto'}}}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={printStatement}
              disabled={rowsWithRunningBalance.length === 0}
            >
              Print Statement
            </Button>
          </Box>
        </Stack>

        {isLoading && (
          <Typography variant="body2" color="primary">Loading statement...</Typography>
        )}

        {!isLoading && isError && (
          <Typography variant="body2" color="error">Could not load statement data.</Typography>
        )}

        {!isLoading && !isError && rowsWithRunningBalance.length === 0 && (
          <Typography variant="body2" color="text.secondary">No entries match the selected filters.</Typography>
        )}

        {!isLoading && !isError && rowsWithRunningBalance.length > 0 && (
          showCards ? (
            <Box sx={{display: 'grid', gap: 1.5}}>
              {rowsWithRunningBalance.map((row) => (
                <ResponsiveRecordCard
                  key={row.id}
                  eyebrow={row.kind === 'debit' ? 'Debit' : 'Credit'}
                  title={row.reference}
                  badge={
                    <Chip
                      size="small"
                      label={row.kind === 'debit' ? 'DEBIT' : 'CREDIT'}
                      sx={{
                        bgcolor: row.kind === 'debit' ? '#fee4e2' : '#dcfae6',
                        color: row.kind === 'debit' ? '#b42318' : '#027a48',
                        fontWeight: 700,
                      }}
                    />
                  }
                  fields={[
                    {label: 'Date', value: <Typography sx={{fontWeight: 600}}>{row.dateLabel}</Typography>},
                    {label: 'Description', value: <Typography sx={{fontWeight: 600}}>{row.description}</Typography>},
                    {label: 'Status', value: <Typography sx={{fontWeight: 600, textTransform: 'capitalize'}}>{row.status === 'paid' ? 'Paid' : row.status === 'unpaid' ? 'Unpaid' : row.status}</Typography>},
                    {label: 'Debit', value: <Typography sx={{fontWeight: 700, color: '#b42318'}}>{row.debit > 0 ? formatCurrency(row.debit) : '—'}</Typography>},
                    {label: 'Credit', value: <Typography sx={{fontWeight: 700, color: '#027a48'}}>{row.credit > 0 ? formatCurrency(row.credit) : '—'}</Typography>},
                    {label: 'Running Balance', value: <Typography sx={{fontWeight: 700}}>{formatCurrency(row.runningBalance)}</Typography>},
                  ]}
                />
              ))}
            </Box>
          ) : (
            <TableContainer sx={{borderRadius: 1, border: '1px solid #e5e7eb'}}>
              <Table>
                <TableHead sx={{backgroundColor: '#f5f7fa'}}>
                  <TableRow>
                    <TableCell sx={{fontWeight: 700}}>Date</TableCell>
                    <TableCell sx={{fontWeight: 700}}>Description</TableCell>
                    <TableCell sx={{fontWeight: 700}}>Reference</TableCell>
                    <TableCell sx={{fontWeight: 700}}>Status</TableCell>
                    <TableCell sx={{fontWeight: 700}}>Debit</TableCell>
                    <TableCell sx={{fontWeight: 700}}>Credit</TableCell>
                    <TableCell sx={{fontWeight: 700}}>Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsWithRunningBalance.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.dateLabel}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={row.kind === 'debit' ? 'D' : 'C'}
                            sx={{
                              bgcolor: row.kind === 'debit' ? '#fee4e2' : '#dcfae6',
                              color: row.kind === 'debit' ? '#b42318' : '#027a48',
                              fontWeight: 700,
                              width: 28,
                            }}
                          />
                          <Typography>{row.description}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.reference}</TableCell>
                      <TableCell>{row.status === 'paid' ? 'Paid' : row.status === 'unpaid' ? 'Unpaid' : row.status}</TableCell>
                      <TableCell sx={{fontWeight: 700, color: '#b42318'}}>{row.debit > 0 ? formatCurrency(row.debit) : '—'}</TableCell>
                      <TableCell sx={{fontWeight: 700, color: '#027a48'}}>{row.credit > 0 ? formatCurrency(row.credit) : '—'}</TableCell>
                      <TableCell sx={{fontWeight: 700}}>{formatCurrency(row.runningBalance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </Stack>
    </Paper>
  );
};

export default LeviesStatement;
